// TrichoAnalysisPage.jsx
import "./TrichoAnalysisPage.scss";

import { Badge, Card, Col, Form, Row, Tab, Tabs } from "react-bootstrap";
import {
  MARK_TYPES,
  buildSummary,
  clamp,
  normalizeRect,
  uid,
} from "./tricho.utils";
import React, { useEffect, useMemo, useState } from "react";
import { formatDateInEN, formatPhone } from "../../../../../commons/util/HallAppUtil";
import { useNavigate, useParams } from "react-router-dom";

import AnalysisTopbar from "./components/AnalysisTopbar";
import AnamneseAPI from "../../api/AnamneseAPI";
import AppButton from "../../../../../commons/buttons/AppButton";
import CustomerAutocompleteV2 from "../../../../commands/components/CustomerAutocomplete";
import EmptyStateTricho from "./components/EmptyStateTricho";
import HallAppToast from "../../../../../commons/HallAppToast";
import ImagePicker from "./components/ImagePicker";
import ImageStage from "./components/ImageStage";
import MarksPanel from "./components/MarksPanel";
import RegionPicker from "./components/RegionPicker";
import ReportHeader from "../../../../report-full/components/ReportHeader";
import SummaryPanel from "./components/SummaryPanel";
import TrichoAPI from "../../api/TrichoAPI";
import { runMockAI } from "./tricho.mockAI";

const trichoAPI = new TrichoAPI();

function safeJsonParse(str) {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function mapDtoToMarksState(dto) {
  const list = Array.isArray(dto?.marks) ? dto.marks : [];
  return list
    .filter((m) => m && m.rect)
    .map((m) => {
      const meta = safeJsonParse(m.metaJson) || {};
      const rect = normalizeRect({
        x: m.rect?.x,
        y: m.rect?.y,
        w: m.rect?.w,
        h: m.rect?.h,
      });

      return {
        id: m.clientId || uid("mk_"),
        type: String(m.type || "SINGLE").toUpperCase(),
        rect,
        score: m.score ?? 1,
        locked: !!m.locked,
        meta: meta || {},
      };
    });
}

async function buildRemoteImageState(url, fileName = "tricho.jpg") {
  try {
    if (!url) return null;

    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
  
    return {
      src: url,
      fileName,
      file: null,
      width: img.naturalWidth || 1200,
      height: img.naturalHeight || 900,
      isRemote: true,
      url,
    };
  } catch(e) {
    console.log(e)
  }
}

function mapDtoToCustomerState(dto) {
  const c = dto?.customer || null;

  const id = c?.id ?? dto?.customerId ?? dto?.clientId ?? null;

  const name =
    c?.name ??
    dto?.customerName ??
    dto?.clientName ??
    dto?.name ??
    "";

  const whatsapp =
    c?.whatsapp ??
    c?.phone ??
    dto?.customerWhatsapp ??
    dto?.whatsapp ??
    dto?.phone ??
    "";

  const birthday = c?.birthday ?? dto?.birthday ?? null;
  const lastVisit = c?.lastVisit ?? dto?.lastVisit ?? null;

  return {
    customerId: id,
    customerName: name || "",
    customerWhatsapp: whatsapp || "",
    birthday,
    lastVisit,
  };
}
const anamneseAPI = new AnamneseAPI();

export default function TrichoAnalysisPage() {
  const { id, anamneseId } = useParams();

  const navigate = useNavigate();
  const isEdit = !!id;

  // -------------------------
  // State
  // -------------------------
  const [image, setImage] = useState(null);
  const [region, setRegion] = useState("FRONTAL");
  const [mode, setMode] = useState("BASIC");
  const [confidence, setConfidence] = useState(0.65);
  const [anamnese, setAnamnese] = useState(null);

  const [analysisName, setAnalysisName] = useState("");
  const [analysisDate, setAnalysisDate] = useState(formatDateInEN(new Date()));

  const [customerId, setCustomerId] = useState(null);
  const [customerWhatsapp, setCustomerWhatsapp] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerBirthday, setCustomerBirthday] = useState(null);
  const [customerLastVisit, setCustomerLastVisit] = useState(null);

  const [marks, setMarks] = useState([]);
  const [activeType, setActiveType] = useState("SINGLE");
  const [selectedId, setSelectedId] = useState(null);

  const [isLoading, setLoading] = useState(false);

  const showAdvanced = mode === "ADV";

  // -------------------------
  // Derived
  // -------------------------
  const visibleMarks = useMemo(() => {
    const filtered = marks.filter((m) => m.type === activeType);
    if (!showAdvanced) return filtered;
    return filtered.filter((m) => (m.score ?? 1) >= confidence);
  }, [marks, activeType, showAdvanced, confidence]);

  const allCounts = useMemo(() => {
    const counts = { SINGLE: 0, DOUBLE: 0, TRIPLE: 0, OSTIUM: 0 };
    const list = showAdvanced
      ? marks.filter((m) => (m.score ?? 1) >= confidence)
      : marks;

    for (const m of list) {
      const t = String(m.type || "").toUpperCase();
      counts[t] = (counts[t] || 0) + 1;
    }
    return counts;
  }, [marks, showAdvanced, confidence]);

  const summary = useMemo(() => {
    return buildSummary({
      counts: allCounts,
      areaCm2: null,
      compareControl: true,
      controlCounts: null,
    });
  }, [allCounts]);

  const severityBadge = useMemo(() => {
    const v = summary?.severity?.variant || "secondary";
    const label = summary?.severity?.label || "—";
    return { v, label };
  }, [summary]);

  const canSave = useMemo(() => {
    const hasImage = !!image?.src;
    const hasFileWhenCreate = !isEdit ? !!image?.file : true;
    const hasCustomer = true;

    return (
      hasCustomer &&
      hasImage &&
      hasFileWhenCreate &&
      !!analysisDate &&
      !!analysisName?.trim()
    );
  }, [image, analysisName, analysisDate, isEdit]);

  // -------------------------
  // Load (Edit)
  // -------------------------
  useEffect(() => {
    loadForAnamnese();
    loadForEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, anamneseId]);

  const loadForAnamnese = async () => {
    if (!anamneseId) return;

    try {
      setLoading(true);
      const resp = await anamneseAPI.getById(anamneseId)
      setAnamnese(resp.data)
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  }

  const loadForEdit = async () => {
    if (!isEdit) return;

    try {
      setLoading(true);

      const res = await trichoAPI.getById(id);
      const dto = res?.data || res;

      setAnalysisName(dto?.title || "");
      setAnalysisDate(dto?.examDate || formatDateInEN(new Date()));
      setMode(dto?.mode || "BASIC");
      setConfidence(dto?.confidence ?? 0.65);

      setRegion(String(dto?.region || "FRONTAL").toUpperCase());

      const c = mapDtoToCustomerState(dto);
      setCustomerId(c.customerId);
      setCustomerName(c.customerName);
      setCustomerWhatsapp(c.customerWhatsapp);
      setCustomerBirthday(c.birthday);
      setCustomerLastVisit(c.lastVisit);

      const remote = await buildRemoteImageState(
        dto?.image?.imageUrl,
        dto?.image?.originalFilename || "tricho.jpg"
      );
      setImage(remote);

      setMarks(mapDtoToMarksState(dto));

      setActiveType("SINGLE");
      setSelectedId(null);
    } catch (e) {
      HallAppToast.genericError();
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };
  // -------------------------
  // Helpers
  // -------------------------
  const resetAll = () => {
    setMarks([]);
    setSelectedId(null);
    setActiveType("SINGLE");
    setConfidence(0.65);
    setMode("BASIC");
    setRegion("FRONTAL");
    setImage(null);

    setAnalysisName("");
    setAnalysisDate(formatDateInEN(new Date()));

    setCustomerId(null);
    setCustomerName("");
    setCustomerWhatsapp("");
    setCustomerBirthday(null);
    setCustomerLastVisit(null);
  };

  const buildSaveTrichoFormData = ({ payload, imageFile }) => {
    const formData = new FormData();

    formData.append(
      "payload",
      new Blob([JSON.stringify(payload)], { type: "application/json" })
    );

    if (imageFile) formData.append("image", imageFile);
    return formData;
  };

  // -------------------------
  // Actions
  // -------------------------
  const onSave = async () => {
    try {
      setLoading(true);

      const payload = {
        customerId: customerId || anamnese?.customerId || null,
        responseId: anamnese?.responseId || null,
        customerName: customerName?.trim() || null,
        customerWhatsapp: customerWhatsapp || null,
        birthday: customerBirthday || null,
        lastVisit: customerLastVisit || null,

        title: analysisName?.trim() || null,
        examDate: analysisDate,
        mode,
        confidence,
        region,

        image: image
          ? {
            originalFilename: image.fileName || null,
            imageUrl: image.isRemote ? image.src : null,
            width: image.width ?? null,
            height: image.height ?? null,
            source: "UPLOAD",
          }
          : null,

        marks: (marks || []).map((m) => ({
          clientId: m.id,
          type: m.type,
          rect: {
            x: m.rect?.x,
            y: m.rect?.y,
            w: m.rect?.w,
            h: m.rect?.h,
          },
          score: m.score ?? null,
          locked: !!m.locked,
          metaJson: m.meta ? JSON.stringify(m.meta) : null,
        })),

        summary,
      };

      const formData = buildSaveTrichoFormData({
        payload,
        imageFile: image?.file || null,
      });

      if (isEdit) {
        await trichoAPI.update(id, formData);
        HallAppToast.success("Análise atualizada com sucesso!");
      } else {
        await trichoAPI.create(formData);
        HallAppToast.success("Análise salva com sucesso!");
        resetAll();
      }
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  };

  const onAddMark = (rect) => {
    if (!image) return;
    const r = normalizeRect(rect);
    if (r.w < 6 || r.h < 6) return;

    const mk = {
      id: uid("mk_"),
      type: activeType,
      rect: r,
      score: 1,
      locked: false,
      meta: { region },
    };

    setMarks((prev) => [...prev, mk]);
    setSelectedId(mk.id);
  };

  const onUpdateRect = (idMark, rectPatch) => {
    setMarks((prev) =>
      prev.map((m) => {
        if (m.id !== idMark) return m;
        const nextRect = normalizeRect({ ...m.rect, ...rectPatch });
        return { ...m, rect: nextRect };
      })
    );
  };

  const onDeleteSelected = () => {
    if (!selectedId) return;
    setMarks((prev) => prev.filter((m) => m.id !== selectedId));
    setSelectedId(null);
  };

  const onRunAI = async () => {
    if (!image?.src) return;

    setLoading(true);
    try {
      if (image?.file) {
        const formData = new FormData();
        formData.append("image", image.file, image.fileName || image.file.name);
        await trichoAPI.generatedIA(formData);
      } else {
        await trichoAPI.generatedIAByUrl(image.src);
      }
    } finally {
      setLoading(false);
    }
  };

  const onPickImage = (img) => {
    setImage(img);
    setMarks([]);
    setSelectedId(null);
  };

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="tricho-shell">
      <div className="container">
        <div className="tricho-page">
          <ReportHeader name={isEdit ? "Editar Tricoscopia" : "Nova Tricoscopia"} />

          <AnalysisTopbar
            title={isEdit ? "Editar Análise Tricoscópica" : "Análise Tricoscópica"}
            badgeVariant={severityBadge.v}
            badgeLabel={severityBadge.label}
            mode={mode}
            onChangeMode={setMode}
            onReset={resetAll}
            analysisName={analysisName}
            setAnalysisName={setAnalysisName}
            analysisDate={analysisDate}
            setAnalysisDate={setAnalysisDate}
            onSave={onSave}
            canSave={canSave && !isLoading}
            isSaving={isLoading}
          />

          {/* Cliente */}
          {anamnese && <Row className="g-3 mb-3">
            <Col xs={12}>
              <Card className="tricho-card tricho-card--soft">
                <Card.Body>
                  <div className="tricho-section-head">
                    <div>
                      <div className="tricho-section-title">Cliente</div>
                    </div>
                  </div>
                
                <h5>{anamnese?.customerName} - {formatPhone(anamnese?.customerWhatsapp)}</h5>

                </Card.Body>
              </Card>
            </Col>
          </Row>}
          {
            !anamnese && <Row className="g-3 mb-3">
              <Col xs={12}>
                <Card className="tricho-card tricho-card--soft">
                  <Card.Body>
                    <div className="tricho-section-head">
                      <div>
                        <div className="tricho-section-title">Cliente</div>
                        <div className="tricho-section-sub">
                          Busque pelo WhatsApp ou selecione na lista
                        </div>
                      </div>

                      {(customerName || customerWhatsapp) && (
                        <div className="tricho-customer-pill">
                          <strong className="me-2">{customerName || "—"}</strong>
                          <span className="tricho-customer-pill__muted">
                            {customerWhatsapp ? formatPhone(customerWhatsapp) : ""}
                          </span>
                        </div>
                      )}
                    </div>

                    <CustomerAutocompleteV2
                      customerId={customerId}
                      value={{ id: customerId, name: customerName, whatsapp: customerWhatsapp }}
                      onChange={(cust) => {
                        setCustomerId(cust?.id ?? null);
                        setCustomerName(cust?.name || "");
                        setCustomerWhatsapp(cust?.whatsapp || "");
                      }}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          }

          <Row className="g-3">
            {/* ESQUERDA */}
            <Col xs={12} lg={8}>
              <Card className="tricho-card tricho-card--soft">
                <Card.Body>
                  <Row className="g-2 align-items-end mb-2">
                    <Col xs={12} md>
                      <RegionPicker region={region} setRegion={setRegion} />
                    </Col>

                    <Col xs={12} md="auto">
                      <ImagePicker onPickImage={onPickImage} />
                    </Col>

                    {/* <Col xs={12} md="auto" className="d-grid">
                      <AppButton
                        size="sm"
                        name="Gerar com IA"
                        onClick={onRunAI}
                        disabled={!image || isLoading}
                      />
                    </Col> */}
                  </Row>

                  {!image ? (
                    <EmptyStateTricho />
                  ) : (
                    <>
                      <div className="tricho-tabs">
                        <Tabs
                          activeKey={activeType}
                          onSelect={(k) => {
                            if (!k) return;
                            setActiveType(k);
                            setSelectedId(null);
                          }}
                          className="mb-2"
                        >
                          {MARK_TYPES.map((t) => (
                            <Tab
                              eventKey={t.key}
                              key={t.key}
                              title={
                                <span className="tricho-tab-title">
                                  {t.label}
                                  <Badge bg="light" text="dark" className="ms-1">
                                    {allCounts[t.key] || 0}
                                  </Badge>
                                </span>
                              }
                            />
                          ))}
                        </Tabs>
                      </div>

                      {showAdvanced && (
                        <div className="tricho-adv-row">
                          <Form.Label className="m-0">
                            Limiar confiança: <strong>{confidence.toFixed(2)}</strong>
                          </Form.Label>

                          <Form.Range
                            min={0.3}
                            max={0.95}
                            step={0.01}
                            value={confidence}
                            onChange={(e) =>
                              setConfidence(clamp(Number(e.target.value), 0.3, 0.95))
                            }
                            disabled={isLoading}
                          />

                          <div className="tricho-adv-hint">
                            Mostrando marcações com score ≥ {confidence.toFixed(2)}
                          </div>
                        </div>
                      )}

                      <ImageStage
                        image={image}
                        marks={visibleMarks}
                        selectedId={selectedId}
                        setSelectedId={setSelectedId}
                        onAddMark={onAddMark}
                        onUpdateRect={onUpdateRect}
                        onDeleteSelected={onDeleteSelected}
                        showScores={showAdvanced}
                        disabled={isLoading}
                      />

                      <MarksPanel
                        activeType={activeType}
                        marks={visibleMarks}
                        selectedId={selectedId}
                        setSelectedId={setSelectedId}
                        onDeleteSelected={onDeleteSelected}
                        showAdvanced={showAdvanced}
                      />
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* DIREITA */}
            <Col xs={12} lg={4}>
              <SummaryPanel
                summary={summary}
                counts={allCounts}
                showAdvanced={showAdvanced}
                confidence={confidence}
              />

              {/* <Card className="tricho-card tricho-card--soft mt-3">
                <Card.Body className="d-grid gap-2">
                  <AppButton
                    size="sm"
                    variant="secondary"
                    isOutline
                    label="Exportar PDF"
                    disabled={!image || isLoading}
                    onClick={() => alert("Export (stub): plugar geração de PDF aqui")}
                  />

                  <AppButton
                    size="sm"
                    variant="secondary"
                    isOutline
                    label="Copiar JSON (debug)"
                    disabled={!image || isLoading}
                    onClick={() => {
                      const payload = {
                        customerId,
                        customerName,
                        customerWhatsapp,
                        image: image?.fileName,
                        region,
                        marks,
                      };
                      navigator.clipboard?.writeText(JSON.stringify(payload, null, 2));
                      alert("JSON copiado para a área de transferência (debug).");
                    }}
                  />
                </Card.Body>
              </Card> */}
            </Col>
          </Row>

          <div className="tricho-footer-note">
            Dica: use <strong>occipital</strong> como área controle quando possível.
          </div>
        </div>
      </div>
    </div>
  );
}